import { X } from 'lucide-react';
import { useMaintenanceWindow } from '@/features/maintenance';
import { useMaintenanceStore } from '@/store/maintenance-store';
import { formatWindowTime } from '@/utils/format';

/** Approved copy (Brian Murray, BFL-1441) with the window's interval filled in (Proweb-38). */
function unavailabilityMessage(startsAt: string, endsAt: string): string {
  return (
    `Our system will be unavailable from ${formatWindowTime(startsAt)} to ${formatWindowTime(endsAt)}. ` +
    'We appreciate your understanding and apologize for any inconvenience.'
  );
}

/** The fallback when the lookup reports a window without its interval. */
const NO_TIMES_MESSAGE =
  'Scheduled maintenance is in progress. Some information may be unavailable.';

/**
 * Maintenance banner (US-018, Proweb-38). Renders nothing unless a window is reported, which is also the
 * fail-open answer when the lookup fails: portal-backend resolves any error to "no window", so a
 * Maintenance-Window outage shows no banner and no error.
 *
 * Dismissal lives in a shared store keyed to the window's identity: the banner is mounted on the login
 * screen — above the Provider ID field — and again in the shell, and a dismissal has to survive the trip
 * between them without also swallowing a different window announced later (US-018 AC-005).
 */
export function MaintenanceBanner(): React.JSX.Element | null {
  const dismissedWindow = useMaintenanceStore((state) => state.dismissedWindow);
  const dismiss = useMaintenanceStore((state) => state.dismiss);
  const { data } = useMaintenanceWindow();

  if (data?.active !== true) {
    return null;
  }

  const windowId = `${data.startsAt ?? ''}|${data.endsAt ?? ''}`;
  if (dismissedWindow === windowId) {
    return null;
  }

  const message =
    data.startsAt !== null && data.endsAt !== null
      ? unavailabilityMessage(data.startsAt, data.endsAt)
      : NO_TIMES_MESSAGE;

  return (
    <div
      role="status"
      className="border-destructive/40 bg-destructive/10 text-destructive border-b"
    >
      <div className="mx-auto flex max-w-shell items-start justify-between gap-4 px-6 py-3 sm:px-10">
        <div className="text-sm">
          <p className="font-semibold">
            <span aria-hidden="true">⚠️ </span>Scheduled Maintenance
          </p>
          <p className="mt-0.5">{message}</p>
        </div>
        <button
          type="button"
          onClick={(): void => {
            dismiss(windowId);
          }}
          aria-label="Dismiss maintenance notice"
          className="flex-none hover:opacity-70"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
